import { Resource } from 'constants/resources';

import { keepPreviousData } from '@tanstack/react-query';
import get from 'lodash/get';
import { useGetOne } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Chip } from 'shared/mui/Chips';
import { useAnchor } from 'shared/mui/DatePicker/hooks/useAnchor';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { FilterChipLabel } from 'shared/mui/Filter/FilterChipsToolbar/FilterChips/components/FilterChipLabel/FilterChipLabel';
import { PopoverChip } from 'shared/mui/Filter/FilterChipsToolbar/FilterChips/components/PopoverChip/PopoverChip';

export type FilterEnumAutocompleteChipProps = {
  label: string;
  resource: Resource;
  name: string;
  children: React.ReactNode;
  onDelete?: () => void;
};

export const FilterEnumAutocompleteChip: React.FC<FilterEnumAutocompleteChipProps> = ({
  resource,
  children,
  name,
  label,
  onDelete,
}) => {
  const { getValues, setValue } = useFormContext();
  const { setAppliedFilter, appliedFilters } = useFilterContext();
  const { handleClose, handleClick, anchorEl } = useAnchor<HTMLDivElement>();
  const filterValue = get(appliedFilters, name);

  const { data, isLoading } = useGetOne(
    resource,
    { id: `admin/${resource}/${filterValue}` },
    { enabled: Boolean(filterValue), placeholderData: keepPreviousData },
  );

  if (!filterValue) {
    return <></>;
  }

  function onApply() {
    handleClose();
    setAppliedFilter(name, getValues(name));
  }

  function onResetHandler() {
    setAppliedFilter(name, filterValue);
    setValue(name, filterValue);
  }

  const open = Boolean(anchorEl);

  return (
    <>
      <PopoverChip
        anchorEl={anchorEl}
        onApply={onApply}
        onClose={handleClose}
        onReset={onResetHandler}
        open={open}
      >
        {children}
      </PopoverChip>
      <Chip
        label={<FilterChipLabel isLoading={isLoading} label={label} value={data?.description} />}
        onClick={handleClick}
        onDelete={() => {
          onDelete && onDelete();
          setValue(name, null);
          setAppliedFilter(name, null);
        }}
        size="medium"
      />
    </>
  );
};
